import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';

export type GeocodeResult = {
  label: string;
  lat: number;
  lng: number;
  confidence: 'exact' | 'approximate';
};

type NominatimSearchResult = {
  lat: string;
  lon: string;
  display_name?: string;
  class?: string;
  type?: string;
  importance?: number;
  place_rank?: number;
};

type NominatimReverseResult = {
  lat?: string;
  lon?: string;
  display_name?: string;
};

@Injectable({ providedIn: 'root' })
export class GeocodingService {
  /**
   * Nominatim (OpenStreetMap) - gratuito, HTTPS, no API key.
   * Docs: https://nominatim.org/release-docs/latest/api/Overview/
   *
   * Nota: Nominatim ha una usage policy (rate-limit). In UI conviene non chiamarlo “ad ogni keypress”,
   * ma solo su azione esplicita (qui: click su "Cerca").
   */
  private readonly nominatimBaseUrl = 'https://nominatim.openstreetmap.org';

  constructor(private http: HttpClient) {}

  search(query: string, limit = 5): Observable<GeocodeResult[]> {
    const q = (query || '').trim();
    if (!q) return of([]);

    const params = new HttpParams()
      .set('q', q)
      .set('format', 'jsonv2')
      .set('addressdetails', '1')
      .set('limit', String(limit))
      .set('accept-language', 'it')
      // di default restringiamo all'Italia (puoi rimuoverlo se vuoi risultati worldwide)
      .set('countrycodes', 'it');

    return this.http
      .get<NominatimSearchResult[]>(`${this.nominatimBaseUrl}/search`, {
        params,
      })
      .pipe(
        map((rows) => {
          const results: GeocodeResult[] = [];
          for (const r of rows || []) {
            const lat = Number(r.lat);
            const lng = Number(r.lon);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
            const label = (r.display_name || 'Risultato').trim();
            const confidence = this.inferConfidenceFromNominatim(r);
            results.push({ label, lat, lng, confidence });
          }
          return results;
        }),
      );
  }

  reverse(lat: number, lng: number): Observable<{ label: string } | null> {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return of(null);
    const params = new HttpParams()
      .set('lat', String(lat))
      .set('lon', String(lng))
      .set('format', 'jsonv2')
      .set('accept-language', 'it');

    return this.http
      .get<NominatimReverseResult>(`${this.nominatimBaseUrl}/reverse`, { params })
      .pipe(
        map((r) => {
          const label = (r?.display_name || '').trim();
          return label ? { label } : null;
        }),
      );
  }

  private inferConfidenceFromNominatim(r: NominatimSearchResult): 'exact' | 'approximate' {
    const cls = (r.class || '').toLowerCase();
    const type = (r.type || '').toLowerCase();
    const placeRank = typeof r.place_rank === 'number' ? r.place_rank : 0;

    // euristica: numeri civici / edifici tendono ad essere precisi
    if (cls === 'building' || type === 'house' || type === 'building' || type === 'house_number') {
      return 'exact';
    }

    // risultati di livello città/paese sono centroid → approssimativi
    if (['city', 'town', 'village', 'municipality', 'county', 'state', 'country'].includes(type)) {
      return 'approximate';
    }

    // place_rank alto = feature più specifica (di solito)
    if (placeRank >= 26) return 'exact';
    return 'approximate';
  }
}

